import * as React from "react"

import { Button } from "../ui/button"
import { Slider } from "../ui/slider"
import { Input } from "../ui/input"
import { Label } from "../ui/label"

import { FormDataProps, BusinessTypesProps } from "../CalculatorTypes"

export interface Step2Props {
    formData: FormDataProps
    config: BusinessTypesProps
    onNext: ({
        visitors,
        spend,
        result,
    }: {
        visitors: number
        spend: number
        result: number
    }) => void
}

/**
 * Step2 – Slider‑ & Input‑Formular mit Live‑Berechnung.
 *
 * Props
 * ────────────────────────────────────────
 * config  (Object):
 *   minVisitors, maxVisitors,
 *   minSpend,    maxSpend
 * onNext  (Function):
 *   wird mit { visitors, spend, result } auf Submit aufgerufen
 */
export default function Step2({ formData, config, onNext }: Step2Props) {
    const {
        minVisitors = 0,
        maxVisitors = 0,
        midVisitors = 0,
        minSpend = 0,
        maxSpend = 0,
    } = config.step2Config

    let visitorQuestion = "Quanti visitatori ti aspetti?"
    let spendQuestion = "Consumo medio per visitatore"

    switch (config.type) {
        case "event":
            visitorQuestion = "Quanti visitatori ti aspetti per il tuo evento?"
            break

        case "installation":
            visitorQuestion = "Quanti visitatori ti aspetti in una stagione?"
            break

        default:
            visitorQuestion = "Quanti visitatori ti aspetti?"
            break
    }

    /* helper: slider-pos (0-100) <-> visitors-value  */
    const toVisitors = (pos: number) =>
        pos <= 50
            ? Math.round(minVisitors + ((midVisitors - minVisitors) * pos) / 50)
            : Math.round(
                  midVisitors + ((maxVisitors - midVisitors) * (pos - 50)) / 50
              )

    const toPos = (val: number) =>
        val <= midVisitors
            ? ((val - minVisitors) / (midVisitors - minVisitors)) * 50
            : 50 + ((val - midVisitors) / (maxVisitors - midVisitors)) * 50

    // separate Raw-Input-Strings, um beliebige Zwischenwerte zuzulassen
    const [visitorsInput, setVisitorsInput] = React.useState(String(minVisitors))
    const [spendInput, setSpendInput] = React.useState(String(minSpend))

    // «gültige» numerische States, nur gesetzt wenn im Bereich
    const [visitors, setVisitors] = React.useState(minVisitors)
    const [spend, setSpend] = React.useState(minSpend)

    const [errorVisitors, setErrorVisitors] = React.useState("")
    const [errorSpend, setErrorSpend] = React.useState("")

    // Grenzen ändern → alles zurücksetzen
    React.useEffect(() => {
        setVisitorsInput(String(formData?.visitors || minVisitors))
        setSpendInput(String(formData?.spend || minSpend))
        setVisitors(formData?.visitors || minVisitors)
        setSpend(formData?.spend || minSpend)
        setErrorVisitors("")
        setErrorSpend("")
    }, [minVisitors, minSpend])

    // —— Validation Helpers ——————————————————————————————
    const inRange = (val: number, min: number, max: number) =>
        val >= min && val <= max

    const handleVisitorsInput = (raw: string) => {
        setVisitorsInput(raw)
        const num = Number(raw)
        if (!Number.isNaN(num) && num >= minVisitors && num <= maxVisitors) {
            setVisitors(num)
            setErrorVisitors("")
        } else {
            setErrorVisitors(`Valore tra ${minVisitors} e ${maxVisitors}`)
        }
    }

    const handleSpendInput = (raw: string) => {
        setSpendInput(raw)
        const num = Number(raw)
        if (!Number.isNaN(num) && inRange(num, minSpend, maxSpend)) {
            setSpend(num)
            setErrorSpend("")
        } else {
            setErrorSpend(`Valore tra ${minSpend} e ${maxSpend}`)
        }
    }

    const handleVisitorsSlider = (pos: number) => {
        const v = toVisitors(pos)
        setVisitors(v)
        setVisitorsInput(String(v))
        setErrorVisitors("")
    }

    const handleSpendSlider = (val: number) => {
        setSpend(val)
        setSpendInput(String(val))
        setErrorSpend("")
    }

    const isValid = !errorVisitors && !errorSpend

    const result = React.useMemo(
        () => (isValid ? visitors * spend : 0),
        [isValid, visitors, spend]
    )

    const handleNext = (e: React.FormEvent) => {
        e.preventDefault()
        if (!isValid) return
        onNext?.({ visitors, spend, result })
    }

    return (
        <form
            onSubmit={handleNext}
            className="step-shadow step2 max-w-md w-full mx-auto p-3 lg:p-6 sm:p-3 space-y-8 flex flex-col items-center justify-center rounded-2xl"
            style={{
                maxWidth: "600px",
            }}
        >
            {/* Slider 1 – Besucher */}
            <div className="space-y-2 mt-2">
                <Label className="">{visitorQuestion}</Label>
                <div className="flex items-center gap-4">
                    <Slider
                        className="flex-1"
                        min={0}
                        max={100}
                        step={1}
                        value={[toPos(visitors)]}
                        onValueChange={([p]) => handleVisitorsSlider(p)}
                    />
                    <Input
                        type="number"
                        className="w-30 calculator-text font-light"
                        value={visitorsInput}
                        onChange={(e) => handleVisitorsInput(e.target.value)}
                    />
                </div>
                {errorVisitors && (
                    <p className="text-red-600 text-sm">{errorVisitors}</p>
                )}
            </div>

            {/* Slider 2 – Ø-Ausgabe */}
            <div className="space-y-2">
                <Label className="">{spendQuestion}</Label>
                <div className="flex items-center gap-4">
                    <Slider
                        className="flex-1"
                        min={minSpend}
                        max={maxSpend}
                        step={1}
                        value={[spend]}
                        onValueChange={([s]) => handleSpendSlider(s)}
                    />
                    <Input
                        type="number"
                        className="w-30 calculator-text font-light"
                        value={spendInput}
                        onChange={(e) => handleSpendInput(e.target.value)}
                    />
                </div>
                {errorSpend && (
                    <p className="text-red-600 text-sm">{errorSpend}</p>
                )}
            </div>

            {/* Ergebnis */}
            <div className="text-center text-xl font-medium">
                Il vostro guadagno senza blytz:
                <br />
                <span className="font-bold ml-2">
                    {isValid
                        ? result.toLocaleString("it-IT", {
                              style: "currency",
                              currency: "EUR",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                          })
                        : "—"}
                </span>
            </div>

            <Button
                type="submit"
                disabled={!isValid}
                size="lg"
                className="w-full calculator-button-text font-medium"
            >
                Avanti
            </Button>
        </form>
    )
}
